import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Phone, ShieldCheck } from "lucide-react";

const COUNTRY_CODES = ["+91", "+1", "+44", "+61", "+971", "+65"];

// Shared WhatsApp-OTP login/signup form used by both LoginPage and RegisterPage.
// The backend endpoint transparently logs an existing phone in or creates a
// brand-new account on first verification — so this one component covers both.
export function PhoneAuthForm({ testidPrefix = "phone-auth" }) {
  const { requestPhoneOtp, verifyPhoneOtp } = useAuth();
  const navigate = useNavigate();
  const [countryCode, setCountryCode] = useState("+91");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("phone"); // "phone" | "otp"
  const [submitting, setSubmitting] = useState(false);

  const sendOtp = async (e) => {
    e.preventDefault();
    if (phoneNumber.length < 6) { toast.error("Enter a valid phone number."); return; }
    setSubmitting(true);
    try {
      await requestPhoneOtp(countryCode, phoneNumber);
      toast.success("We sent a 6-digit code on WhatsApp.");
      setStage("otp");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setSubmitting(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error("Enter the full 6-digit code."); return; }
    setSubmitting(true);
    try {
      await verifyPhoneOtp(countryCode, phoneNumber, code);
      toast.success("You're in! Welcome to Junior Shelf.");
      navigate("/browse", { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setSubmitting(false); }
  };

  if (stage === "otp") {
    return (
      <form onSubmit={verify} className="space-y-4" data-testid={`${testidPrefix}-otp-form`}>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2 block">
            Code sent to {countryCode} {phoneNumber} on WhatsApp
          </label>
          <div className="relative">
            <ShieldCheck size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="input-warm pl-10 tracking-widest font-display text-lg"
              data-testid={`${testidPrefix}-otp-input`}
            />
          </div>
        </div>
        <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid={`${testidPrefix}-otp-submit`}>
          {submitting ? "Verifying…" : "Verify & continue"}
        </button>
        <button
          type="button"
          onClick={() => { setStage("phone"); setCode(""); }}
          className="text-sm text-slate-500 hover:text-slate-700 w-full text-center"
          data-testid={`${testidPrefix}-otp-back`}
        >
          Use a different number
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={sendOtp} className="space-y-4" data-testid={`${testidPrefix}-phone-form`}>
      <div>
        <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2 block">WhatsApp number</label>
        <div className="flex gap-2">
          <select
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            className="input-warm w-24 flex-shrink-0"
            data-testid={`${testidPrefix}-country-code`}
          >
            {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="relative flex-1">
            <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="tel"
              required
              inputMode="numeric"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 15))}
              placeholder="98xxxxxxxx"
              className="input-warm pl-10"
              data-testid={`${testidPrefix}-phone-input`}
            />
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">We'll send a 6-digit code on WhatsApp — no password needed.</p>
      </div>
      <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid={`${testidPrefix}-phone-submit`}>
        {submitting ? "Sending…" : "Send WhatsApp code"}
      </button>
    </form>
  );
}
